import { defineStore } from "pinia";
import {
  useDataStore,
  useUIStore,
  usePurchaseFlowStore,
  useCrudStore,
  useAuthStore,
  useSchemaStore,
} from "@/stores";
import { extractSigningLinkFromResponse, generateRandomId } from "@/utilities";
import { SUITABILITY_QUESTIONNAIRE_GROUPS } from "@/constants";

export const useChangeInvestmentStore = defineStore("changeInvestmentStore", {
  state: () => ({
    current_step: 1,
    current_substep: 1,
    change_investment_steps: null,
    current_investment_for_change: null,
    change_investment_children: [],
    conversion_agreement_doc: null,
    conversion_documents: [],
    childrenSnapshot: null,
    pollingInterval: null,
  }),

  getters: {
    currentStep(state) {
      if (state.current_step > 0 && state.change_investment_steps) {
        return state.change_investment_steps[state.current_step - 1];
      }
      return null;
    },
    hasSigningProgress(state) {
      return !!state.conversion_agreement_doc || state.conversion_documents.length > 0;
    },
    hasChildrenChanged(state) {
      if (!state.childrenSnapshot) return false;
      const current = state.change_investment_children.map((c) => ({
        record_id: c.record_id,
        amount: Number(c.amount) || 0,
        is_compounded: c.is_compounded?.value ?? c.is_compounded,
        offering_id: c.offering?.value?.record_id ?? null,
      }));
      return JSON.stringify(current) !== JSON.stringify(state.childrenSnapshot);
    },
    allDocumentsSigned(state) {
      const allChildrenSigned = state.change_investment_children.every((child) => {
        if (!child.record_id) return false;
        const doc = state.conversion_documents.find((d) => d.investment_id === child.record_id);
        return doc?.is_completed === true;
      });
      const conversionSigned = state.conversion_agreement_doc?.is_completed === true;
      return allChildrenSigned && conversionSigned;
    },
    currentStep(state) {
      if (state.current_step > 0 && state.change_investment_steps) {
        return state.change_investment_steps[state.current_step - 1];
      }
      return null;
    },
    investableAmount(state) {
      if (!state.current_investment_for_change) return 0;
      return Math.floor(state.current_investment_for_change.current_nav / 1000) * 1000;
    },
    paidBackAmount(state) {
      if (!state.current_investment_for_change) return 0;
      return +(state.current_investment_for_change.current_nav - this.investableAmount).toFixed(2);
    },
    parentTermMonths(state) {
      return state.current_investment_for_change?.bond_term ?? 0;
    },
    availableOfferings() {
      const dataStore = useDataStore();
      return dataStore.offerings
        .filter((o) => o.type === "S1" && o.bond_term >= this.parentTermMonths)
        .sort((a, b) => a.bond_term - b.bond_term);
    },
    availableTermMonths() {
      return [...new Set(this.availableOfferings.map((o) => o.bond_term))].sort((a, b) => a - b);
    },
    parentComboKey() {
      return `${this.parentTermMonths}-${this.current_investment_for_change?.is_compounded}`;
    },
    allValidChildCombos() {
      const combos = [];
      for (const term of this.availableTermMonths) {
        for (const compounded of [false, true]) {
          const key = `${term}-${compounded}`;
          if (key !== this.parentComboKey) {
            combos.push({ term, compounded, key });
          }
        }
      }
      return combos;
    },
    maxSplits() {
      return this.allValidChildCombos.length;
    },
    canAddSplit(state) {
      return state.change_investment_children.length < this.maxSplits;
    },
    totalChildrenAmount(state) {
      return state.change_investment_children.reduce((sum, child) => {
        return sum + (Number(child.amount) || 0);
      }, 0);
    },
    remainingAmount() {
      return this.investableAmount - this.totalChildrenAmount;
    },
    splitValidation(state) {
      const errors = [];

      // 1. Total must exactly equal investable amount
      if (this.totalChildrenAmount > this.investableAmount) {
        const over = this.totalChildrenAmount - this.investableAmount;
        errors.push(
          `Total exceeds the investable amount by $${over.toLocaleString()}. Please reduce your amounts.`
        );
      } else if (this.totalChildrenAmount < this.investableAmount) {
        const under = this.investableAmount - this.totalChildrenAmount;
        errors.push(
          `$${under.toLocaleString()} remaining — the entire amount of $${this.investableAmount.toLocaleString()} must be allocated.`
        );
      }

      // 2. Each child: amount must be > 0 and a multiple of $1,000
      state.change_investment_children.forEach((child, i) => {
        const amt = Number(child.amount) || 0;
        if (amt <= 0) {
          errors.push(`Investment ${i + 1} must have an amount greater than $0.`);
        } else if (amt % 1000 !== 0) {
          errors.push(
            `Investment ${i + 1} amount ($${amt.toLocaleString()}) must be in increments of $1,000.`
          );
        }
      });

      // 3. Each child must have an offering selected
      state.change_investment_children.forEach((child, i) => {
        if (!child.offering?.value) {
          errors.push(`Investment ${i + 1} must have a term/yield selected.`);
        }
      });

      // 4. No child can match the parent's exact term + compounding type
      state.change_investment_children.forEach((child, i) => {
        if (!child.offering?.value) return;
        const childKey = `${child.offering.value.bond_term}-${child.is_compounded.value}`;
        if (childKey === this.parentComboKey) {
          const termYears = child.offering.value.bond_term / 12;
          const type = child.is_compounded.value ? "at maturity" : "monthly";
          errors.push(
            `Investment ${i + 1} is ${termYears}-year ${type} — the same as the current investment being changed. Choose a different term or interest type.`
          );
        }
      });

      // 5. No two children can share the same term + compounding type
      const seen = new Set();
      state.change_investment_children.forEach((child, i) => {
        if (!child.offering?.value) return;
        const key = `${child.offering.value.bond_term}-${child.is_compounded.value}`;
        if (seen.has(key)) {
          const termYears = child.offering.value.bond_term / 12;
          const type = child.is_compounded.value ? "at maturity" : "monthly";
          errors.push(
            `Duplicate: you already have a ${termYears}-year ${type} investment. Each term can only have one of each interest type.`
          );
        }
        seen.add(key);
      });

      // 6. Term must be >= parent term
      state.change_investment_children.forEach((child, i) => {
        if (!child.offering?.value) return;
        if (child.offering.value.bond_term < this.parentTermMonths) {
          errors.push(
            `Investment ${i + 1} has a shorter term than your current investment. Only equal or longer terms are allowed.`
          );
        }
      });

      return {
        is_valid: errors.length === 0,
        errors,
      };
    },
    isSplitValid() {
      return this.splitValidation.is_valid;
    },
    showSplitError(state) {
      const validation = this.splitValidation;
      if (validation.is_valid) return false;

      // Immediate errors — show right away
      const hasImmediateError = validation.errors.some(
        (e) =>
          e.startsWith("Duplicate") || e.includes("exceeds") || e.includes("same as the current")
      );
      if (hasImmediateError) return true;

      // Deferred errors — only show once every child has an amount > 0
      const allHaveAmounts = state.change_investment_children.every((c) => Number(c.amount) > 0);
      if (allHaveAmounts) return true;

      return false;
    },
    visibleErrors() {
      if (!this.showSplitError) return [];
      return this.splitValidation.errors;
    },
  },

  actions: {
    async initializeChangeInvestmentFlow(investment_id) {
      const dataStore = useDataStore();
      const purchaseFlowStore = usePurchaseFlowStore();

      this._completionStarted = false;
      this.conversion_agreement_doc = null;
      this.conversion_documents = [];
      this.childrenSnapshot = null;
      this.stopPollingDocuments();

      const investment_for_change = dataStore.investments.find(
        (inv) => inv.record_id === investment_id
      );
      if (!investment_for_change) {
        throw new Error("Investment not found");
      }
      this.current_investment_for_change = investment_for_change;

      // Try to fetch existing children from tempinvestments
      const existingChildren = await this.fetchExistingChildInvestments(investment_id);

      if (existingChildren.length > 0) {
        // console.log(
        //   "Existing child investments found for this change investment. Rehydrating state with persisted data from tempinvestments.",
        //   existingChildren
        // );
        // Rehydrate with dropdown objects from persisted flat data
        this.change_investment_children = existingChildren.map((child) => {
          const offering = dataStore.offerings.find((o) => o.record_id === child.offering_id);
          return {
            ...child,
            is_compounded: {
              value: child.is_compounded,
              name: child.is_compounded ? "At Maturity" : "Monthly",
            },
            offering: offering
              ? {
                  name: `${offering.interest_rate}% (${offering.bond_term / 12} year term)`,
                  value: offering,
                }
              : null,
          };
        });
      } else {
        // Fresh start — default first child
        // console.log(
        //   "No existing child investments found for this change investment. Initializing with default first child offering."
        // );
        const availableS1Offerings = dataStore.offerings
          .filter(
            (offering) =>
              offering.type === "S1" && offering.bond_term >= investment_for_change.bond_term
          )
          .sort((a, b) => b.bond_term - a.bond_term);

        const defaultOffering = availableS1Offerings[0];
        const defaultAmount = Math.floor(investment_for_change.current_nav / 1000) * 1000;

        this.change_investment_children = [
          {
            record_id: null,
            amount: defaultAmount,
            is_compounded: {
              value: !investment_for_change.is_compounded,
              name: !investment_for_change.is_compounded ? "At Maturity" : "Monthly",
            },
            offering: defaultOffering
              ? {
                  name: `${defaultOffering.interest_rate}% (${defaultOffering.bond_term / 12} year term)`,
                  value: defaultOffering,
                }
              : null,
          },
        ];
      }

      this.current_step = 1;
      this.current_substep = 1;
      purchaseFlowStore.cloneNeededSchema();
      this.setInvestmentStepsForChangeInvestment();
      this.snapshotChildren();
    },
    setInvestmentStepsForChangeInvestment() {
      this.change_investment_steps = [
        { label: "Change Investment", name: "Change Investment" },
        {
          label: "Suitability Questionnaire",
          name: "Suitability Questionnaire",
          sub_steps: SUITABILITY_QUESTIONNAIRE_GROUPS.map((g) => g.name),
        },
        { label: "Sign and Finalize", name: "Sign and Finalize" },
      ];
    },
    addNewInvestmentForSplit() {
      const UIStore = useUIStore();
      if (!this.canAddSplit) {
        UIStore.animateNotificationAlert({
          type: "error",
          message: `You can only have up to ${this.maxSplits} splits due to the available offerings and your current investment's bond term/payment frequency. Please remove an existing split to add another.`,
          does_persist: true,
        });
        return;
      }

      const taken = new Set(
        this.change_investment_children
          .filter((c) => c.offering?.value)
          .map((c) => `${c.offering.value.bond_term}-${c.is_compounded.value}`)
      );

      // Pick first untaken valid combo
      let defaultOffering = null;
      let defaultCompounded = false;

      for (const combo of this.allValidChildCombos) {
        if (!taken.has(combo.key)) {
          const offering = this.availableOfferings.find((o) => o.bond_term === combo.term);
          if (offering) {
            defaultOffering = {
              name: `${offering.interest_rate}% (${offering.bond_term / 12} year term)`,
              value: offering,
            };
            defaultCompounded = {
              value: combo.compounded,
              name: combo.compounded ? "At Maturity" : "Monthly",
            };
            break;
          }
        }
      }

      this.change_investment_children.push({
        amount: 0,
        is_compounded: defaultCompounded,
        offering: defaultOffering,
      });
    },
    removeInvestmentSplit(index) {
      if (this.change_investment_children.length <= 1) return;
      this.change_investment_children.splice(index, 1);
    },
    async handleInsertOrUpdateChildInvestments() {
      const crudStore = useCrudStore();
      const dataStore = useDataStore();
      const schemaStore = useSchemaStore();

      const parent = this.current_investment_for_change;

      try {
        const existingInDb = await this.fetchExistingChildInvestments(parent.record_id);
        const currentRecordIds = new Set(
          this.change_investment_children.map((c) => c.record_id).filter(Boolean)
        );

        const orphanIds = existingInDb
          .filter((dbChild) => !currentRecordIds.has(dbChild.record_id))
          .map((dbChild) => dbChild.record_id);

        if (orphanIds.length > 0) {
          // console.log("Deleting orphaned temp children:", orphanIds);

          // Delete their tempdocuments first
          await crudStore.delete("tempdocuments", [
            { field_name: "investment_id", operator: "in", value: orphanIds },
            { field_name: "type", operator: "eq", value: "Sub Agreements" },
          ]);

          // Delete the orphaned tempinvestments
          await crudStore.delete("tempinvestments", [
            { field_name: "record_id", operator: "in", value: orphanIds },
          ]);
        }

        for (const child of this.change_investment_children) {
          const hasExistingRecord = !!child.record_id;
          const amount = Number(child.amount) || 0;

          let record = {
            ...child,
            amount,
            bonds: amount / 1000,
            current_nav: amount,
            discounted_bond_amount: amount,
            offering_id: child.offering?.value?.record_id ?? null,
            offering_name: child.offering?.value?.name ?? null,
            offering_type: child.offering?.value?.type ?? "S1",
            offering_issuer: child.offering?.value?.issuer ?? null,
            is_compounded: child.is_compounded?.value ?? child.is_compounded,
            payment_method: "Conversion",
            is_conversion: true,
            conversion_parent_ids: [parent.record_id],
            status: "Created",

            // Inherited from parent
            type: parent.type,
            entity_account_id: parent.entity_account_id,
            joint_contact_id: parent.joint_contact_id,
            owner_contact_full_name: parent.owner_contact_full_name,
            custodian_id: parent.custodian_id,
            custodian_account_number: parent.custodian_account_number,
            distribution_method: parent.distribution_method,
            distribution_account: parent.distribution_account,
          };

          record = schemaStore.addCreatedUpdatedFields(record, false, true);

          if (!hasExistingRecord) {
            // console.log("Inserting new child investment into tempinvestments with data:", record);
            const prepared = dataStore.setDefaultFields(record);
            await crudStore.insert("tempinvestments", [prepared]);
            child.record_id = prepared.record_id;
          } else {
            // console.log(
            //   "Updating existing child investment in tempinvestments with record_id:",
            //   child.record_id
            // );
            await crudStore.update(
              "tempinvestments",
              [{ field_name: "record_id", operator: "eq", value: child.record_id }],
              record
            );
          }
        }

        // console.log("Child investments saved to tempinvestments.");
      } catch (err) {
        throw new Error(`Failed to save child investments: ${err.message}`);
      }
    },
    async fetchExistingChildInvestments(parent_record_id) {
      const crudStore = useCrudStore();

      const conversionChildren = await crudStore.find(
        "tempinvestments",
        [{ field_name: "conversion_parent_ids", operator: "contains", value: [parent_record_id] }],
        null,
        "created_date"
      );

      // console.log("Fetched existing child investments from tempinvestments:", conversionChildren);

      return conversionChildren || [];
    },
    async fetchConversionDocuments() {
      const crudStore = useCrudStore();

      // Capture existing statuses before overwriting
      const existingStatuses = {};
      for (const doc of [...this.conversion_documents, this.conversion_agreement_doc].filter(
        Boolean
      )) {
        if (doc.sign_request_id) {
          existingStatuses[doc.sign_request_id] = doc.pending_joint_signature || false;
        }
      }

      const childRecordIds = this.change_investment_children
        .map((c) => c.record_id)
        .filter(Boolean);

      if (childRecordIds.length > 0) {
        const tempDocuments = await crudStore.find("tempdocuments", [
          { field_name: "investment_id", operator: "in", value: childRecordIds },
          { field_name: "type", operator: "eq", value: "Sub Agreements" },
        ]);
        this.conversion_documents = (tempDocuments || []).map((doc) => ({
          ...doc,
          pending_joint_signature: existingStatuses[doc.sign_request_id] || false,
        }));
      }

      if (this.current_investment_for_change?.record_id) {
        const conversionRequestForm = await crudStore.find("tempdocuments", [
          {
            field_name: "investment_ids",
            operator: "contains",
            value: [this.current_investment_for_change.record_id],
          },
          { field_name: "file_category", operator: "eq", value: "Conversion Request Form" },
        ]);
        const doc = conversionRequestForm?.[0] || null;
        if (doc) {
          doc.pending_joint_signature = existingStatuses[doc.sign_request_id] || false;
        }
        this.conversion_agreement_doc = doc;
      }

      await this.checkSignerStatuses();
    },
    async generateSigningLinkForChild(child_record_id) {
      // console.log("Generating signing link for child investment with record_id:", child_record_id);
      const authStore = useAuthStore();
      const UIStore = useUIStore();

      try {
        const response = await fetch(
          `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/generate-signing-links-for-change-investments`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              session_id: authStore.current_user.session_id,
              user_id: authStore.current_user?.id,
            },
            body: JSON.stringify({
              investment_id: child_record_id,
              parent_investment_id: this.current_investment_for_change.record_id,
            }),
          }
        );

        if (!response.ok) {
          UIStore.animateNotificationAlert({
            message:
              "There was an error generating the subscription agreement! Please refresh your page and try again.",
            icon: "ShieldAlert",
            type: "error",
            does_persist: true,
          });
          return null;
        }

        const data = await response.json();
        const signingLink = extractSigningLinkFromResponse(data);

        if (!signingLink) {
          UIStore.animateNotificationAlert({
            message:
              "There was an error generating the subscription agreement! Please refresh your page and try again.",
            icon: "ShieldAlert",
            type: "error",
            does_persist: true,
          });
          return null;
        }

        return signingLink;
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    async generateSigningLinkForConversionAgreement() {
      const authStore = useAuthStore();
      const UIStore = useUIStore();

      try {
        const response = await fetch(
          `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/generate-conversion-request-form-for-change`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              session_id: authStore.current_user.session_id,
              user_id: authStore.current_user?.id,
            },
            body: JSON.stringify({
              parent_investment_id: this.current_investment_for_change.record_id,
              child_investment_ids: this.change_investment_children.map((c) => c.record_id),
            }),
          }
        );

        if (!response.ok) {
          UIStore.animateNotificationAlert({
            message:
              "There was an error generating the conversion agreement! Please refresh your page and try again.",
            icon: "ShieldAlert",
            type: "error",
            does_persist: true,
          });
          return null;
        }

        const data = await response.json();
        const signingLink = extractSigningLinkFromResponse(data);

        if (!signingLink) {
          UIStore.animateNotificationAlert({
            message:
              "There was an error generating the conversion agreement! Please refresh your page and try again.",
            icon: "ShieldAlert",
            type: "error",
            does_persist: true,
          });
          return null;
        }

        return signingLink;
      } catch (err) {
        console.error(err);
        return null;
      }
    },
    async checkSignerStatuses() {
      const authStore = useAuthStore();
      const current_user = authStore.current_user;
      const isJoint = this.current_investment_for_change?.type === "Joint";

      if (!isJoint) return;

      for (const doc of [...this.conversion_documents, this.conversion_agreement_doc].filter(
        Boolean
      )) {
        if (doc.is_completed || !doc.sign_request_id) continue;

        try {
          const response = await fetch(
            `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/get-signer-status`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                user_id: current_user.id,
                session_id: current_user.session_id,
              },
              body: JSON.stringify({ signRequestId: doc.sign_request_id }),
            }
          );
          const data = await response.json();
          doc.pending_joint_signature = data?.data?.pending_joint_signature || false;
        } catch (err) {
          console.error("Error fetching signer status:", err);
        }
      }
    },
    startPollingDocuments() {
      if (this.pollingInterval) return;

      this.pollingInterval = setInterval(async () => {
        // If we've already started the completion flow, don't run another poll
        if (this._completionStarted) return;

        await this.fetchConversionDocuments();

        const allChildrenSigned = this.change_investment_children.every((child) => {
          if (!child.record_id) return false;
          const doc = this.conversion_documents.find((d) => d.investment_id === child.record_id);
          return doc?.is_completed === true;
        });

        const conversionSigned = this.conversion_agreement_doc?.is_completed === true;

        if (allChildrenSigned && conversionSigned) {
          this._completionStarted = true;
          this.stopPollingDocuments();
          await this.handleConversionComplete();
        }
      }, 5000);
    },
    stopPollingDocuments() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async resumeFromRedirect(parent_record_id) {
      const UIStore = useUIStore();
      try {
        // Re-initialize the store from persisted tempinvestments
        await this.initializeChangeInvestmentFlow(parent_record_id);

        // Override children with what's already in the DB
        const existingChildren = await this.fetchExistingChildInvestments(parent_record_id);

        if (existingChildren.length > 0) {
          const dataStore = useDataStore();

          this.change_investment_children = existingChildren.map((child) => {
            const offering = dataStore.offerings.find((o) => o.record_id === child.offering_id);
            return {
              ...child,
              is_compounded: {
                value: child.is_compounded,
                name: child.is_compounded ? "At Maturity" : "Monthly",
              },
              offering: offering
                ? {
                    name: `${offering.interest_rate}% (${offering.bond_term / 12} year term)`,
                    value: offering,
                  }
                : null,
            };
          });
        }

        // Jump to final step
        this.current_step = 3;
        this.current_substep = 1;

        // Fetch docs and start polling
        await this.fetchConversionDocuments();
        this.startPollingDocuments();
      } catch (err) {
        console.error("Error resuming change investment flow:", err);
      } finally {
        UIStore.is_loading = false;
      }
    },
    async clearConversionProgress() {
      const crudStore = useCrudStore();
      const parent = this.current_investment_for_change;

      try {
        const childRecordIds = this.change_investment_children
          .map((c) => c.record_id)
          .filter(Boolean);

        if (childRecordIds.length > 0) {
          // Delete S1 agreement tempdocuments for all children
          await crudStore.delete("tempdocuments", [
            { field_name: "investment_id", operator: "in", value: childRecordIds },
            { field_name: "type", operator: "eq", value: "Sub Agreements" },
          ]);

          // Delete tempinvestments
          await crudStore.delete("tempinvestments", [
            { field_name: "record_id", operator: "in", value: childRecordIds },
          ]);
        }

        // Delete conversion request form (tied to parent)
        if (parent?.record_id) {
          await crudStore.delete("tempdocuments", [
            { field_name: "investment_ids", operator: "contains", value: [parent.record_id] },
            { field_name: "file_category", operator: "eq", value: "Conversion Request Form" },
          ]);
        }

        // Reset in-memory state
        this.change_investment_children = [];
        this.conversion_documents = [];
        this.conversion_agreement_doc = null;
        this.stopPollingDocuments();

        // console.log("Cleared all conversion progress for parent:", parent?.record_id);
      } catch (err) {
        console.error("Failed to clear conversion progress:", err);
        throw err;
      }
    },
    snapshotChildren() {
      this.childrenSnapshot = this.change_investment_children.map((c) => ({
        record_id: c.record_id,
        amount: Number(c.amount) || 0,
        is_compounded: c.is_compounded?.value ?? c.is_compounded,
        offering_id: c.offering?.value?.record_id ?? null,
      }));
    },
    async handleConversionComplete() {
      //  console.log("Running COmplete conversion after all documents signed");
      const UIStore = useUIStore();
      const dataStore = useDataStore();
      const parentRecordId = this.current_investment_for_change.record_id;

      UIStore.is_loading = true;

      try {
        // Wait for backend finalization to complete (parent gets is_pending_conversion)
        let finalized = false;
        for (let i = 0; i < 10; i++) {
          await dataStore.setData(["investments", "documents"], true);

          const parent = dataStore.investments.find((inv) => inv.record_id === parentRecordId);
          if (parent?.is_pending_conversion) {
            finalized = true;
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 3000));
        }

        if (!finalized) {
          console.warn("Backend finalization not detected after polling. Redirecting anyway.");
        }
      } catch (err) {
        console.error("Error during post-finalization cleanup:", err);
      }

      window.location.href = `/investment-details?recordId=${parentRecordId}`;
    },
  },
});
